import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../material.module';
import { SpacerService } from '../../../services/spacer.service';

export interface SpacerAppendNoteDialogData {
  spacerId: number;
  originalNote: string;
}

export interface SpacerAppendNoteDialogResult {
  success: boolean;
  updatedNotes?: string;
}

@Component({
  selector: 'app-spacer-append-note-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  templateUrl: './spacer-append-note-dialog.component.html',
  styleUrls: ['./spacer-append-note-dialog.component.css']
})
export class SpacerAppendNoteDialogComponent {
  noteToAppend = '';
  isWorking = false;
  errorMessage = '';

  get formattedOldNote(): string {
    if (!this.data.originalNote) {
      return '';
    }
    
    // Split by newlines, reverse to show newest first, and rejoin
    return this.data.originalNote
      .split(/\r?\n/)
      .filter(line => line.trim().length > 0)
      .reverse()
      .join('\n');
  }

  constructor(
    private readonly spacerService: SpacerService,
    private readonly dialogRef: MatDialogRef<SpacerAppendNoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: SpacerAppendNoteDialogData
  ) { }

  onAppend(): void {
    const note = this.noteToAppend.trim();
    if (!note) {
      return;
    }

    this.errorMessage = '';
    this.isWorking = true;

    this.spacerService.appendNote(this.data.spacerId, note).subscribe({
      next: (response) => {
        this.isWorking = false;
        if (response.success) {
          // After successful append, close dialog with success
          this.dialogRef.close({ success: true, updatedNotes: response.updatedNotes });
        } else {
          this.errorMessage = response.message || 'Failed to append note.';
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.getErrorMessage(error, 'Failed to append note. Please try again.');
        this.isWorking = false;
      }
    });
  }

  onClear(): void {
    const shouldClear = window.confirm('Are you sure you want to clear all old Notes?');
    if (!shouldClear) {
      return;
    }

    this.errorMessage = '';
    this.isWorking = true;

    this.spacerService.clearNote(this.data.spacerId).subscribe({
      next: (response) => {
        this.isWorking = false;
        if (response.success) {
          // After successful clear, close dialog with success and empty notes
          this.dialogRef.close({ success: true, updatedNotes: response.updatedNotes || '' });
        } else {
          this.errorMessage = response.message || 'Failed to clear notes.';
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.getErrorMessage(error, 'Failed to clear notes. Please try again.');
        this.isWorking = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  private getErrorMessage(error: HttpErrorResponse, fallback: string): string {
    return error?.error?.detail ?? error?.error?.title ?? error?.message ?? fallback;
  }
}
